import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Briefcase,
  MapPin,
  DollarSign,
  Search,
  Building2,
  Users,
  Zap,
  Heart,
  Coffee,
  Laptop,
  ArrowRight,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import WebsiteLayout from '@/components/layout/WebsiteLayout';

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applicationData, setApplicationData] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    cover_letter: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, typeFilter]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs?status=active`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];
    if (searchTerm) {
      filtered = filtered.filter(
        (job) =>
          job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (typeFilter !== 'all') {
      filtered = filtered.filter((job) => job.job_type === typeFilter);
    }
    setFilteredJobs(filtered);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!applicationData.applicant_name || !applicationData.applicant_email || !applicationData.applicant_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        job_id: selectedJob.id,
        job_title: selectedJob.title,
        ...applicationData
      });
      toast.success('Application submitted successfully!');
      setApplyDialogOpen(false);
      setApplicationData({ applicant_name: '', applicant_email: '', applicant_phone: '', cover_letter: '' });
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { icon: Laptop, title: 'Remote Work', desc: 'Work from anywhere' },
    { icon: Clock, title: 'Flexible Hours', desc: 'Work-life balance' },
    { icon: Heart, title: 'Health Benefits', desc: 'Full coverage' },
    { icon: Coffee, title: 'Team Events', desc: 'Regular activities' },
    { icon: Zap, title: 'Growth', desc: 'Learn & develop' },
    { icon: Users, title: 'Great Team', desc: 'Talented people' },
  ];

  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Remote', 'Internship'];

  return (
    <WebsiteLayout>
      {/* Hero Section with Image */}
      <section className="pt-32 pb-16 bg-slate-900 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85" 
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900/70"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-400 font-semibold mb-4 tracking-wide uppercase text-sm">Join Our Team</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Career Opportunities
              </h1>
              <p className="text-xl text-slate-400 max-w-2xl">
                Join our team of talented professionals and help shape the future of digital transformation.
              </p>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Modern workspace"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-gradient-to-b from-slate-900 to-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Why Work With Us?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center p-4 rounded-xl bg-slate-800/50">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-white text-sm">{benefit.title}</h3>
                  <p className="text-xs text-slate-400 mt-1">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search jobs by title or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-white hover:bg-slate-700">All Types</SelectItem>
                {jobTypes.map((type) => (
                  <SelectItem key={type} value={type} className="text-white hover:bg-slate-700">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-slate-400 mb-6">
            Showing {filteredJobs.length} open position{filteredJobs.length !== 1 ? 's' : ''}
          </p>

          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : filteredJobs.length > 0 ? (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <Card key={job.id} className="border border-slate-700 hover:border-slate-600 hover:shadow-xl hover:shadow-blue-500/10 transition-all bg-slate-900">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white mb-1">{job.title}</h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-3">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />{job.company}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />{job.location}
                              </span>
                              {job.salary_range && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-4 h-4" />{job.salary_range}
                                </span>
                              )}
                            </div>
                            <p className="text-slate-400 line-clamp-2">{job.description}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {job.job_type}
                        </Badge>
                        <Button
                          onClick={() => { setSelectedJob(job); setApplyDialogOpen(true); }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No positions found</h3>
              <p className="text-slate-400">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </section>

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply for {selectedJob?.title}</DialogTitle>
            <DialogDescription>
              Submit your application for this position at {selectedJob?.company}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <Input
                placeholder="Your full name"
                value={applicationData.applicant_name}
                onChange={(e) => setApplicationData({ ...applicationData, applicant_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={applicationData.applicant_email}
                onChange={(e) => setApplicationData({ ...applicationData, applicant_email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <Input
                placeholder="+60 12-345 6789"
                value={applicationData.applicant_phone}
                onChange={(e) => setApplicationData({ ...applicationData, applicant_phone: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cover Letter</label>
              <Textarea
                placeholder="Tell us why you're a great fit..."
                rows={4}
                value={applicationData.cover_letter}
                onChange={(e) => setApplicationData({ ...applicationData, cover_letter: e.target.value })}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setApplyDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </WebsiteLayout>
  );
}
